import React from 'react';
import {
  Alert, Box, Button, FormControl, FormControlLabel, InputLabel,
  MenuItem, Paper, Select, Stack, Switch, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { StargraveCrewData, StargraveExperienceAchievements, StargraveLeader, StargraveLootResolution } from '../../types';
import { useStargraveSoldiers } from '../../stores/stargraveCustomStore';
import { stargravePermanentInjuries } from '../../data/stargrave/permanentInjuries';
import { stargravePowers } from '../../data/stargrave/coreRules';
import { stargraveAdvancedWeapons } from '../../data/stargrave/advancedWeapons';
import { stargraveAdvancedTechnology } from '../../data/stargrave/advancedTechnology';
import { stargraveAlienArtefacts } from '../../data/stargrave/alienArtefacts';
import { PowerCard } from '../shared/PowerCard';
import { getShipUpgradePowerHints } from '../../game/stargraveUpkeepRules';
import {
  canAddPermanentInjury,
  calculateStargraveExperience,
  StargraveSurvivalResolution,
} from '../../game/stargraveCampaignRules';
import { isStargraveLootResolutionComplete, resolveStargraveLoot } from '../../game/stargraveLootRules';

interface KnockedOutMember {
  id: string;
  name: string;
  role: 'captain' | 'firstMate' | 'soldier';
  leader?: StargraveLeader;
}

interface StargravePostGameTabProps {
  crewData: StargraveCrewData;
  onComplete: (
    resolutions: StargraveSurvivalResolution[],
    experience: number,
    credits: number,
    lootResolutions: StargraveLootResolution[],
    freeRobots: Array<{ profileId: string; name?: string }>
  ) => void;
}

const outcomeLabels = {
  dead: 'Dead',
  permanentInjury: 'Permanent Injury',
  badlyWounded: 'Badly Wounded',
  closeCall: 'Close Call',
  fullRecovery: 'Full Recovery',
};
const leaderOutcomes = ['dead', 'permanentInjury', 'badlyWounded', 'closeCall', 'fullRecovery'] as const;
const soldierOutcomes = ['dead', 'badlyWounded', 'fullRecovery'] as const;

export const StargravePostGameTab: React.FC<StargravePostGameTabProps> = ({ crewData, onComplete }) => {
  const stargraveSoldiers = useStargraveSoldiers();
  const [resolutions, setResolutions] = React.useState<Record<string, StargraveSurvivalResolution>>({});
  const [credits, setCredits] = React.useState(0);
  const [enforceExperienceLimits, setEnforceExperienceLimits] = React.useState(false);
  const [lootResolutions, setLootResolutions] = React.useState<Record<string, StargraveLootResolution>>({});
  const [resolvedPowers, setResolvedPowers] = React.useState<string[]>([]);
  const [robotRecruits, setRobotRecruits] = React.useState<Record<string, { profileId: string; name: string }>>({});
  const hasMedic = crewData.soldiers.some((soldier) => soldier.profileId === 'medic');
  const outOfGameAPowers = [crewData.captain, crewData.firstMate]
    .filter((leader): leader is StargraveLeader => Boolean(leader))
    .flatMap((leader) => leader.powers.flatMap((selection) => {
      const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
      if (!power || !power.timing.includes('Out of Game (A)')) return [];
      return [{ key: `${leader.id}:${power.id}`, leader, power, selection }];
    }));
  const knockedOut: KnockedOutMember[] = [
    ...(crewData.captain?.gameState.currentHealth === 0
      ? [{ id: crewData.captain.id, name: crewData.captain.name, role: 'captain' as const, leader: crewData.captain }]
      : []),
    ...(crewData.firstMate?.gameState.currentHealth === 0
      ? [{ id: crewData.firstMate.id, name: crewData.firstMate.name, role: 'firstMate' as const, leader: crewData.firstMate }]
      : []),
    ...crewData.soldiers.filter((soldier) => soldier.gameState.currentHealth === 0).map((soldier) => ({
      id: soldier.id,
      name: soldier.name,
      role: 'soldier' as const,
    })),
  ];
  const securedLoot = [
    ...(crewData.captain?.gameState.leftTable && crewData.captain.gameState.currentHealth > 0 && crewData.captain.gameState.loot
      ? [{ memberId: crewData.captain.id, memberName: crewData.captain.name, lootType: crewData.captain.gameState.loot }] : []),
    ...(crewData.firstMate?.gameState.leftTable && crewData.firstMate.gameState.currentHealth > 0 && crewData.firstMate.gameState.loot
      ? [{ memberId: crewData.firstMate.id, memberName: crewData.firstMate.name, lootType: crewData.firstMate.gameState.loot }] : []),
    ...crewData.soldiers.filter((soldier) => soldier.gameState.leftTable && soldier.gameState.currentHealth > 0 && soldier.gameState.loot)
      .map((soldier) => ({ memberId: soldier.id, memberName: soldier.name, lootType: soldier.gameState.loot! })),
  ];
  const [achievements, setAchievements] = React.useState<StargraveExperienceAchievements>({
    gamesPlayed: 1,
    hadFriendlyKnockout: knockedOut.length > 0,
    lootUnlocked: 0,
    powersActivated: 0,
    rivalCrewKnockouts: 0,
    creatureKnockouts: 0,
    scenarioBonus: 0,
  });
  const rawExperience = calculateStargraveExperience({
    ...achievements,
    hadFriendlyKnockout: knockedOut.length > 0,
  });
  const experience = calculateStargraveExperience({
    ...achievements,
    hadFriendlyKnockout: knockedOut.length > 0,
  }, enforceExperienceLimits);
  const updateAchievement = (field: keyof StargraveExperienceAchievements, value: number) => {
    setAchievements((current) => ({ ...current, [field]: Math.max(0, Math.floor(value)) }));
  };

  const updateResolution = (memberId: string, changes: Partial<StargraveSurvivalResolution>) => {
    setResolutions((current) => ({
      ...current,
      [memberId]: { ...current[memberId], memberId, ...changes } as StargraveSurvivalResolution,
    }));
  };

  const getOutcome = (member: KnockedOutMember) => {
    return resolutions[member.id]?.outcome ?? null;
  };

  const complete = knockedOut.every((member) => {
    const outcome = getOutcome(member);
    return Boolean(outcome && (outcome !== 'permanentInjury' || resolutions[member.id]?.injuryId));
  }) && securedLoot.every((loot) => {
    const resolution = lootResolutions[loot.memberId];
    return Boolean(resolution && isStargraveLootResolutionComplete(resolution));
  });
  const updateLootResolution = (memberId: string, lootType: 'physical' | 'data', changes: Partial<StargraveLootResolution>) => {
    setLootResolutions((current) => ({
      ...current,
      [memberId]: { ...current[memberId], memberId, lootType, roll: current[memberId]?.roll ?? 0, ...changes },
    }));
  };

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant="h5">After Game</Typography>
    <Typography color="text.secondary">Resolve every knocked-out crew member before completing recovery.</Typography>
    {knockedOut.length === 0 && <Alert severity="success">No crew members require a Survival Roll.</Alert>}

    {knockedOut.map((member) => {
      const resolution = resolutions[member.id];
      const outcome = getOutcome(member);
      const availableInjuries = stargravePermanentInjuries.filter((injury) =>
        canAddPermanentInjury(member.leader?.permanentInjuryIds ?? [], injury)
      );
      const soldierProfile = member.role === 'soldier'
        ? stargraveSoldiers.find((profile) => profile.id === crewData.soldiers.find((soldier) => soldier.id === member.id)?.profileId)
        : null;

      return <Paper key={member.id} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{member.name}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {member.role === 'captain' ? 'Captain' : member.role === 'firstMate' ? 'First Mate' : soldierProfile?.name ?? 'Soldier'}
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Survival Outcome</Typography>
        <ToggleButtonGroup exclusive value={outcome ?? ''}
          onChange={(_, value) => value && updateResolution(member.id, { outcome: value })}
          sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {(member.role === 'soldier' ? soldierOutcomes : leaderOutcomes).map((candidate) => (
            <ToggleButton key={candidate} value={candidate} sx={{ flex: '1 1 auto' }}>{outcomeLabels[candidate]}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        {outcome === 'permanentInjury' && <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id={`injury-${member.id}`}>Permanent Injury</InputLabel>
          <Select labelId={`injury-${member.id}`} label="Permanent Injury" value={resolution?.injuryId ?? ''}
            onChange={(event) => updateResolution(member.id, { injuryId: event.target.value })}>
            {availableInjuries.map((injury) => <MenuItem key={injury.id} value={injury.id}>
              <Box>
                <Typography>{injury.name}</Typography>
                <Typography variant="caption" color="text.secondary">{injury.summary}</Typography>
              </Box>
            </MenuItem>)}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the result manually because the printed core table overlaps roll 6.
          </Typography>
        </FormControl>}

        {outcome === 'badlyWounded' && member.role !== 'soldier' && <Alert severity="info" sx={{ mt: 2 }}>
          Starts the next game at half Health unless treatment ({hasMedic ? 50 : 100}cr) is paid during pre-game setup.
        </Alert>}
        {outcome === 'dead' && member.role === 'captain' && <Alert severity="error" sx={{ mt: 2 }}>Captain death ends this crew’s campaign.</Alert>}
        {outcome === 'closeCall' && <Alert severity="warning" sx={{ mt: 2 }}>Assigned Cargo will be lost; General Equipment is replaced free.</Alert>}
      </Paper>;
    })}

    {securedLoot.length > 0 && <Typography variant="h6">Secured Loot</Typography>}
    {securedLoot.map((loot) => {
      const resolution = lootResolutions[loot.memberId];
      const reward = resolution?.roll ? (() => {
        try { return resolveStargraveLoot(resolution); } catch { return null; }
      })() : null;
      const rareItems = reward?.kind === 'advancedWeapon'
        ? stargraveAdvancedWeapons
        : reward?.kind === 'advancedTechnology'
          ? stargraveAdvancedTechnology.filter((item) => item.table === reward.technologyTable)
          : reward?.kind === 'alienArtefact' ? stargraveAlienArtefacts : [];
      return <Paper key={loot.memberId} variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6">{loot.memberName}: {loot.lootType === 'data' ? 'Data-loot' : 'Physical-loot'}</Typography>
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id={`loot-roll-${loot.memberId}`}>Loot d20 Result</InputLabel>
          <Select labelId={`loot-roll-${loot.memberId}`} label="Loot d20 Result" value={resolution?.roll || ''}
            onChange={(event) => updateLootResolution(loot.memberId, loot.lootType, {
              roll: Number(event.target.value), secondaryRoll: undefined, informationChoice: undefined,
              secretChoice: undefined, itemId: undefined,
            })}>
            {Array.from({ length: 20 }, (_, index) => index + 1).map((roll) => <MenuItem key={roll} value={roll}>{roll}</MenuItem>)}
          </Select>
        </FormControl>
        {reward && <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 700 }}>
          Result: {reward.kind === 'advancedTechnology' ? `Advanced Technology Table ${reward.technologyTable}` : reward.kind.replace(/([A-Z])/g, ' $1')}
          {reward.amount ? ` (${reward.amount}cr)` : ''}
        </Typography>}
        {resolution && [2, 4, 6].includes(resolution.roll) && loot.lootType === 'data' && <TextField
          size="small" type="number" label="Secondary d20" value={resolution.secondaryRoll ?? ''} sx={{ mt: 2 }}
          onChange={(event) => updateLootResolution(loot.memberId, loot.lootType, { secondaryRoll: Number(event.target.value) })}
          slotProps={{ htmlInput: { min: 1, max: 20 } }} />}
        {reward?.kind === 'information' && <ToggleButtonGroup exclusive value={resolution?.informationChoice ?? ''}
          onChange={(_, value) => value && updateLootResolution(loot.memberId, loot.lootType, { informationChoice: value })}
          sx={{ mt: 2 }}>
          <ToggleButton value="credits">Sell for {reward.amount}cr</ToggleButton>
          <ToggleButton value="experience">Trade for 20 XP</ToggleButton>
        </ToggleButtonGroup>}
        {reward?.kind === 'secret' && <ToggleButtonGroup exclusive value={resolution?.secretChoice ?? ''}
          onChange={(_, value) => value && updateLootResolution(loot.memberId, loot.lootType, { secretChoice: value })}
          sx={{ mt: 2 }}>
          <ToggleButton value="store">Store on Ship</ToggleButton>
          <ToggleButton value="credits">Sell for {reward.amount}cr</ToggleButton>
        </ToggleButtonGroup>}
        {rareItems.length > 0 && <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel id={`loot-item-${loot.memberId}`}>Item</InputLabel>
          <Select labelId={`loot-item-${loot.memberId}`} label="Item" value={resolution?.itemId ?? ''}
            onChange={(event) => updateLootResolution(loot.memberId, loot.lootType, { itemId: event.target.value })}>
            {rareItems.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </Select>
        </FormControl>}
      </Paper>;
    })}

    {outOfGameAPowers.length > 0 && <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Out of Game (A) Powers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select each Power resolved after the game. Its rule effect should be applied to the crew as appropriate.
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {outOfGameAPowers.map(({ key, leader, power, selection }) => {
          const selected = resolvedPowers.includes(key);
          return (
            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', '& > *': { flex: 1 }, borderRadius: 1, outline: selected ? '2px solid' : 'none', outlineColor: 'primary.main' }}>
              <PowerCard
                name={`${power.name} — ${leader.name}`}
                activation={selection.activation}
                strain={selection.strain}
                timing={power.timing}
                summary={power.summary}
                hints={getShipUpgradePowerHints(crewData, power.id)}
                onClick={() => setResolvedPowers((previous) =>
                  selected ? previous.filter((candidate) => candidate !== key) : [...previous, key])}
              />
            </Box>
          );
        })}
      </Box>
      {(() => {
        const resolvedCreateRobots = outOfGameAPowers.filter(({ key, power }) =>
          power.id === 'create-robot' && resolvedPowers.includes(key));
        if (resolvedCreateRobots.length === 0) return null;
        const profiles = stargraveSoldiers.filter((profile) => profile.id !== 'armoured-trooper');
        const survivingSoldiers = crewData.soldiers.filter((soldier) =>
          !soldier.isTemporary && resolutions[soldier.id]?.outcome !== 'dead');
        const specialistLimit = 4 + (crewData.ship.upgradeIds.includes('extra-quarters') ? 1 : 0);
        const selectedProfiles = resolvedCreateRobots
          .map(({ key }) => profiles.find((profile) => profile.id === robotRecruits[key]?.profileId))
          .filter((profile): profile is NonNullable<typeof profile> => Boolean(profile));
        const totalAfter = survivingSoldiers.length + selectedProfiles.length;
        const specialistsAfter = survivingSoldiers
          .filter((soldier) => stargraveSoldiers.find((profile) => profile.id === soldier.profileId)?.category === 'specialist').length
          + selectedProfiles.filter((profile) => profile.category === 'specialist').length;
        return <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Create Robot</Typography>
          <Stack spacing={1.5}>
            {resolvedCreateRobots.map(({ key, leader }) => (
              <Stack key={key} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id={`create-robot-${key}`}>Robot soldier ({leader.name})</InputLabel>
                  <Select labelId={`create-robot-${key}`} label={`Robot soldier (${leader.name})`}
                    value={robotRecruits[key]?.profileId ?? ''}
                    onChange={(event) => setRobotRecruits((previous) => ({
                      ...previous,
                      [key]: { profileId: String(event.target.value), name: previous[key]?.name ?? '' },
                    }))}>
                    <MenuItem value="">None</MenuItem>
                    {profiles.map((profile) => <MenuItem key={profile.id} value={profile.id}>
                      {profile.name} ({profile.category === 'specialist' ? 'Specialist' : 'Standard'})
                    </MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Name (optional)" value={robotRecruits[key]?.name ?? ''}
                  disabled={!robotRecruits[key]?.profileId}
                  onChange={(event) => setRobotRecruits((previous) => ({
                    ...previous,
                    [key]: { profileId: previous[key]?.profileId ?? '', name: event.target.value },
                  }))} />
              </Stack>
            ))}
          </Stack>
          {totalAfter > 8 && <Alert severity="warning" sx={{ mt: 1.5 }}>
            The crew would have {totalAfter} soldiers, above the normal limit of 8. The limit is not enforced.
          </Alert>}
          {specialistsAfter > specialistLimit && <Alert severity="warning" sx={{ mt: 1.5 }}>
            The crew would have {specialistsAfter} specialists, above the limit of {specialistLimit}. The limit is not enforced.
          </Alert>}
        </Box>;
      })()}
    </Paper>}

    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Rewards</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1">Experience: {experience} XP</Typography>
        <FormControlLabel control={<Switch checked={enforceExperienceLimits}
          onChange={(event) => setEnforceExperienceLimits(event.target.checked)} />}
          label="Enforce core XP limits" />
      </Stack>
      {enforceExperienceLimits && rawExperience !== experience && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Calculated {rawExperience} XP; awarding {experience} XP after core limits.
      </Typography>}
      {rawExperience > 300 && <Alert severity="warning" sx={{ mb: 2 }}>
        The core book recommends a maximum of 300 XP per game. {enforceExperienceLimits ? 'The award is capped.' : 'The full award will be applied.'}
      </Alert>}
      {achievements.powersActivated > 10 && <Alert severity="warning" sx={{ mb: 1 }}>
        The core-book Power activation award is normally limited to 100 XP. {enforceExperienceLimits ? 'The category cap is applied.' : 'The full entered amount will be applied.'}
      </Alert>}
      {achievements.rivalCrewKnockouts > 4 && <Alert severity="warning" sx={{ mb: 1 }}>
        The core-book rival crew knockout award is normally limited to 40 XP. {enforceExperienceLimits ? 'The category cap is applied.' : 'The full entered amount will be applied.'}
      </Alert>}
      {achievements.creatureKnockouts > 4 && <Alert severity="warning" sx={{ mb: 1 }}>
        The core-book creature knockout award is normally limited to 20 XP. {enforceExperienceLimits ? 'The category cap is applied.' : 'The full entered amount will be applied.'}
      </Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <TextField type="number" label="Loot Tokens Unlocked" value={achievements.lootUnlocked}
          onChange={(event) => updateAchievement('lootUnlocked', Number.parseInt(event.target.value, 10) || 0)}
          helperText="20 XP each" />
        <TextField type="number" label="Powers Activated" value={achievements.powersActivated}
          onChange={(event) => updateAchievement('powersActivated', Number.parseInt(event.target.value, 10) || 0)}
          helperText="10 XP each; core limit 100 XP" />
        <TextField type="number" label="Rival Crew Knockouts" value={achievements.rivalCrewKnockouts}
          onChange={(event) => updateAchievement('rivalCrewKnockouts', Number.parseInt(event.target.value, 10) || 0)}
          helperText="10 XP each; core limit 40 XP" />
        <TextField type="number" label="Creature Knockouts" value={achievements.creatureKnockouts}
          onChange={(event) => updateAchievement('creatureKnockouts', Number.parseInt(event.target.value, 10) || 0)}
          helperText="5 XP each; core limit 20 XP" />
        <TextField type="number" label="Scenario Bonus XP" value={achievements.scenarioBonus}
          onChange={(event) => updateAchievement('scenarioBonus', Number.parseInt(event.target.value, 10) || 0)}
          helperText="Awards over 300 XP are allowed with a warning" />
        <TextField type="number" label="Credits Gained" value={credits}
          onChange={(event) => setCredits(Math.max(0, Number.parseInt(event.target.value, 10) || 0))} />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption">Game played: +30 XP</Typography>
        {knockedOut.length > 0 && <Typography variant="caption">Friendly knockout: +30 XP</Typography>}
      </Stack>
    </Paper>

    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button variant="contained" disabled={!complete}
        onClick={() => onComplete(
          Object.values(resolutions),
          experience,
          credits,
          Object.values(lootResolutions),
          outOfGameAPowers
            .filter(({ key, power }) => power.id === 'create-robot' && resolvedPowers.includes(key))
            .flatMap(({ key }) => {
              const recruit = robotRecruits[key];
              return recruit?.profileId ? [{ profileId: recruit.profileId, name: recruit.name.trim() || undefined }] : [];
            })
        )}>Complete After Game</Button>
    </Box>
  </Box>;
};
